import { motion } from 'framer-motion'
import { ChevronsLeft, LogOut } from 'lucide-react'
import { NavLink, useNavigate } from 'react-router-dom'
import { navItems } from '../lib/nav'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useUI } from '../context/UIContext'
import { cn } from '../lib/cn'
import { Tooltip } from './Tooltip'

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUI()
  const { showToast } = useToast()
  const { hasRole, logout } = useAuth()
  const navigate = useNavigate()
  const visibleNavItems = navItems.filter((item) => item.to !== '/admin' || hasRole('PARTNER'))

  async function handleSignOut() {
    await logout()
    showToast('You have been signed out.', 'info')
    navigate('/login', { replace: true })
  }

  return (
    <motion.aside
      animate={{ width: sidebarCollapsed ? 76 : 248 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 hidden lg:flex h-screen shrink-0 flex-col border-r border-shell-border bg-shell overflow-hidden"
    >
      <div className={cn('flex items-center gap-2.5 px-3 py-5', sidebarCollapsed && 'justify-center px-2')}>
        {/* The logo's wordmark is solid black, so it needs a light chip
         * behind it to stay legible on this dark sidebar. */}
        <div className={cn('flex shrink-0 items-center justify-center rounded-lg bg-white', sidebarCollapsed ? 'px-1.5 py-1' : 'px-3 py-2.5')}>
          <img
            src="/studiohappens-logo.svg"
            alt="Studiohappens"
            className={cn('w-auto object-contain', sidebarCollapsed ? 'h-6' : 'h-16')}
          />
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 scrollbar-thin">
        {visibleNavItems.map((item) => {
          const content = (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  sidebarCollapsed && 'justify-center px-0',
                  isActive ? 'text-brand-600' : 'text-shell-text hover:bg-shell-border/50 hover:text-shell-text-active',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="sidebar-active"
                      className="absolute inset-0 rounded-xl bg-brand-50"
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  <item.icon size={18} strokeWidth={2} className="relative z-10 shrink-0" />
                  {!sidebarCollapsed && <span className="relative z-10 whitespace-nowrap">{item.label}</span>}
                  {isActive && !sidebarCollapsed && (
                    <span className="relative z-10 ml-auto h-1.5 w-1.5 rounded-full bg-brand-500" />
                  )}
                </>
              )}
            </NavLink>
          )
          return sidebarCollapsed ? (
            <Tooltip key={item.to} label={item.label}>
              {content}
            </Tooltip>
          ) : (
            content
          )
        })}
      </nav>

      <div className="border-t border-shell-border p-3">
        <button
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-shell-text transition-colors hover:bg-shell-border/50 hover:text-shell-text-active',
            sidebarCollapsed && 'justify-center px-0',
          )}
        >
          <ChevronsLeft size={18} className={cn('shrink-0 transition-transform duration-300', sidebarCollapsed && 'rotate-180')} />
          {!sidebarCollapsed && 'Collapse'}
        </button>
        {!sidebarCollapsed && (
          <button
            onClick={handleSignOut}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-shell-text transition-colors hover:bg-shell-border/50 hover:text-shell-text-active"
          >
            <LogOut size={18} className="shrink-0" />
            Sign out
          </button>
        )}
      </div>
    </motion.aside>
  )
}
